import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import type { DeliveryScheduleItemOut } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { DispatchTable } from '../components/DispatchTable'
import { PageHeader } from '../components/Layout'
import { ScheduleDialog } from '../components/ScheduleDialog'
import { SelectionBar } from '../components/SelectionBar'
import { Panel } from '../components/States'
import { changeToPickup, fetchAllItems, forEach, setItemDate } from '../lib/deliveries'
import { count, formatDate, fullName } from '../lib/format'
import { ensureChequesCleared, useDispatch } from '../lib/useDispatch'

const NO_PARISH = 'No parish set'

export function DeliveryInboxPage() {
  const { isAdmin } = useAuth()
  const [params, setParams] = useSearchParams()
  const parish = params.get('parish') ?? ''
  const search = (params.get('q') ?? '').trim().toLowerCase()
  const retriesOnly = params.get('retries') === '1'
  const [scheduling, setScheduling] = useState(false)
  const dispatch = useDispatch()

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const inbox = useQuery({
    queryKey: ['dispatch', 'inbox'],
    queryFn: () => fetchAllItems({ status: 'unscheduled' }),
    refetchInterval: 60_000,
  })

  const all = inbox.data ?? []
  const parishes = useMemo(
    () => [...new Set(all.map((item) => item.delivery_parish || NO_PARISH))].sort(),
    [all],
  )

  const visible = all.filter((item) => {
    if (parish && (item.delivery_parish || NO_PARISH) !== parish) return false
    if (retriesOnly && !item.previously_attempted) return false
    if (search) {
      const haystack = [
        item.invoice_reference,
        fullName(item.customer_first_name, item.customer_last_name),
        item.delivery_address,
      ]
        .join(' ')
        .toLowerCase()
      if (!haystack.includes(search)) return false
    }
    return true
  })

  // Parish first, like the app — a truck goes to one area.
  const groups = new Map<string, DeliveryScheduleItemOut[]>()
  for (const item of visible) {
    const key = item.delivery_parish || NO_PARISH
    groups.set(key, [...(groups.get(key) ?? []), item])
  }
  const ordered = [...groups.entries()].sort(([a], [b]) =>
    a === NO_PARISH ? 1 : b === NO_PARISH ? -1 : a.localeCompare(b),
  )

  const chosen = all.filter((item) => dispatch.selected.has(item.id))
  const oldest = all.reduce<string | null>(
    (min, item) => (min === null || item.created_at < min ? item.created_at : min),
    null,
  )

  async function startScheduling() {
    try {
      if (await ensureChequesCleared(chosen, isAdmin, dispatch.setNotice)) setScheduling(true)
    } catch (err) {
      dispatch.setNotice({ tone: 'error', text: err instanceof Error ? err.message : 'Could not clear the cheque.' })
    }
  }

  async function schedule(date: string, qtys: Record<string, number>) {
    await dispatch.run(`${chosen.length} item${chosen.length === 1 ? '' : 's'} scheduled for ${formatDate(date)}. Assign a driver on the plan for that day.`, () =>
      forEach(chosen, (item) => {
        const qty = qtys[item.id]
        return setItemDate(item.id, date, qty !== undefined && qty < (item.qty ?? 1) ? qty : undefined)
      }),
    )
    setScheduling(false)
  }

  async function toPickup() {
    if (!window.confirm(`Change ${chosen.length} item${chosen.length === 1 ? '' : 's'} from delivery to counter pickup?`)) return
    await dispatch
      .run(`${chosen.length} item${chosen.length === 1 ? '' : 's'} changed to counter pickup.`, () =>
        forEach(chosen, changeToPickup),
      )
      .catch(() => undefined)
  }

  return (
    <>
      <PageHeader
        title="Delivery inbox"
        subtitle={
          inbox.data
            ? `${count(all.length)} unscheduled item${all.length === 1 ? '' : 's'}${oldest ? ` · oldest waiting since ${formatDate(oldest)}` : ''}`
            : 'Approved deliveries waiting for a date'
        }
      >
        <SelectionBar
          count={chosen.length}
          busy={dispatch.busy}
          hint="Tick items to schedule them or change them to pickup"
          onClear={dispatch.clear}
        >
          <button type="button" className="btn" onClick={toPickup}>
            Change to pickup
          </button>
          <button type="button" className="btn primary" onClick={startScheduling}>
            Schedule delivery…
          </button>
        </SelectionBar>
      </PageHeader>

      <div className="content stack">
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: 1, minWidth: 220 }}>
            <label htmlFor="q">Invoice, customer or address</label>
            <input
              id="q"
              type="search"
              value={params.get('q') ?? ''}
              onChange={(event) => setParam('q', event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="parish">Parish</label>
            <select id="parish" value={parish} onChange={(event) => setParam('parish', event.target.value)}>
              <option value="">All parishes</option>
              {parishes.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <label className="row" style={{ alignItems: 'center', gap: 6, paddingBottom: 8 }}>
            <input
              type="checkbox"
              checked={retriesOnly}
              onChange={(event) => setParam('retries', event.target.checked ? '1' : '')}
            />
            Re-attempts only
          </label>
        </div>

        {dispatch.notice ? (
          <div className={`alert ${dispatch.notice.tone}`}>{dispatch.notice.text}</div>
        ) : null}

        <Panel
          isLoading={inbox.isPending}
          error={inbox.error}
          isEmpty={visible.length === 0}
          emptyText={all.length ? 'Nothing matches these filters.' : 'The inbox is empty — every delivery has a date.'}
        >
          {ordered.map(([name, items]) => (
            <div className="card" key={name}>
              <div className="card-head">
                <h2>{name}</h2>
                <span className="hint">
                  {count(items.length)} item{items.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="card-body flush">
                <DispatchTable items={items} selected={dispatch.selected} onToggle={dispatch.toggle} />
              </div>
            </div>
          ))}
        </Panel>

      </div>

      {scheduling ? (
        <ScheduleDialog
          title={`Schedule ${chosen.length} item${chosen.length === 1 ? '' : 's'}`}
          confirmLabel="Schedule"
          items={chosen}
          onCancel={() => setScheduling(false)}
          onConfirm={schedule}
        />
      ) : null}
    </>
  )
}
